import React, { useState } from 'react';
import { CheckCircle, ChevronDown, ArrowRight, Bell } from 'lucide-react';
import AltHeader from '../components/AltHeader';
import AltFooter from '../components/AltFooter';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';
import GooglePlayIcon from '../components/GooglePlayIcon';
import AffiliateGiftBanner from '../components/AffiliateGiftBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Inline brand glyphs — kept in this file because they're only used here. */
const AppleIcon = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" {...props}>
    <path d="M16.365 1.43c0 1.14-.43 2.21-1.16 3.01-.79.86-2.06 1.52-3.05 1.43-.13-1.13.41-2.27 1.13-3.01.81-.85 2.21-1.49 3.08-1.43zM20.5 17.27c-.55 1.27-.81 1.83-1.51 2.96-.99 1.59-2.39 3.57-4.12 3.59-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.74-.02-3.06-1.83-4.05-3.42-2.78-4.45-3.07-9.67-1.36-12.45 1.21-1.96 3.13-3.11 4.92-3.11 1.83 0 2.98 1 4.5 1 1.47 0 2.36-1 4.48-1 1.6 0 3.3.87 4.51 2.38-3.97 2.18-3.32 7.85.73 8.05z"/>
  </svg>
);
const WindowsIcon = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" {...props}>
    <path d="M3 5.557 11 4.4v7.6H3V5.557zM12 4.247 21 3v9h-9V4.247zM3 13h8v7.6L3 19.443V13zm9 0h9v8.997L12 20.747V13z"/>
  </svg>
);

const COUNTRY_CODES = [
  { code: '+54', country: 'Argentina' },
  { code: '+61', country: 'Australia' },
  { code: '+43', country: 'Austria' },
  { code: '+32', country: 'Belgium' },
  { code: '+55', country: 'Brazil' },
  { code: '+1', country: 'Canada' },
  { code: '+86', country: 'China' },
  { code: '+420', country: 'Czech Republic' },
  { code: '+45', country: 'Denmark' },
  { code: '+358', country: 'Finland' },
  { code: '+33', country: 'France' },
  { code: '+49', country: 'Germany' },
  { code: '+30', country: 'Greece' },
  { code: '+852', country: 'Hong Kong' },
  { code: '+36', country: 'Hungary' },
  { code: '+91', country: 'India' },
  { code: '+353', country: 'Ireland' },
  { code: '+972', country: 'Israel' },
  { code: '+39', country: 'Italy' },
  { code: '+81', country: 'Japan' },
  { code: '+52', country: 'Mexico' },
  { code: '+31', country: 'Netherlands' },
  { code: '+64', country: 'New Zealand' },
  { code: '+47', country: 'Norway' },
  { code: '+48', country: 'Poland' },
  { code: '+351', country: 'Portugal' },
  { code: '+40', country: 'Romania' },
  { code: '+7', country: 'Russia' },
  { code: '+966', country: 'Saudi Arabia' },
  { code: '+65', country: 'Singapore' },
  { code: '+27', country: 'South Africa' },
  { code: '+82', country: 'South Korea' },
  { code: '+34', country: 'Spain' },
  { code: '+46', country: 'Sweden' },
  { code: '+41', country: 'Switzerland' },
  { code: '+90', country: 'Turkey' },
  { code: '+380', country: 'Ukraine' },
  { code: '+971', country: 'United Arab Emirates' },
  { code: '+44', country: 'United Kingdom' },
  { code: '+1', country: 'United States' },
];

const PLATFORMS = [
  {
    key: 'android',
    name: 'Google Play',
    Icon: GooglePlayIcon,
    href: 'https://play.google.com/store/apps/details?id=life.levanta.app',
    status: 'Available now',
    cta: 'Get it on Google Play',
    available: true,
  },
  { key: 'ios', name: 'App Store', Icon: AppleIcon, href: 'https://apps.apple.com/us/app/levanta-life/id6762063448', status: 'Available now', cta: 'Download on the App Store', available: true },
  { key: 'windows', name: 'Windows', Icon: WindowsIcon, status: 'Coming soon', cta: 'Notify me', available: false },
];

const DownloadApp = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    countryCode: '+1',
    phoneNumber: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const submitData = {
      full_name: formData.full_name,
      email: formData.email,
      phone: `${formData.countryCode} ${formData.phoneNumber}`,
    };
    try {
      const response = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Something went wrong');
      setSuccess(true);
      setFormData({ full_name: '', email: '', countryCode: '+1', phoneNumber: '' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white overflow-x-hidden">
      <AltHeader />

      {/* HERO */}
      <section className="relative pt-[110px] md:pt-[170px] pb-14 md:pb-24 px-6 overflow-hidden min-h-[580px] md:min-h-[760px]">
        <AltParallaxBackdrop />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <span
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#0b1f3a]/80 backdrop-blur-md text-xs md:text-[13px] font-semibold tracking-[0.18em] uppercase text-white shadow-lg ring-1 ring-white/20 mb-7"
            data-testid="download-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 inline-block" />
            Download Levanta
          </span>
          <h1
            className="text-4xl md:text-7xl font-black leading-[1.02] tracking-tight text-[#0b1f3a] alt-hero-glow"
          >
            Open the app.{' '}
            <em className="italic font-light text-blue-600">Open the system.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#0b1f3a]/75 max-w-2xl mx-auto leading-relaxed alt-hero-glow">
            Now live on Google Play and the App Store. Windows is coming soon —
            join the waitlist below and we&rsquo;ll write the moment it ships.
          </p>
        </div>
      </section>

      {/* AFFILIATE GIFT BANNER — shown above the platform tiles when the
          visitor landed with ?ref=CODE. Silent no-op otherwise. */}
      <AffiliateGiftBanner />

      {/* PLATFORM TILES */}
      <section className="px-6 pt-10 pb-20 md:pt-14 md:pb-24 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-5">
            {PLATFORMS.map(({ key, name, Icon, href, status, cta, available }) => (
              <div
                key={key}
                className={`relative rounded-3xl p-7 md:p-8 ring-1 transition-all duration-300 ${
                  available
                    ? 'bg-[#0b1f3a] text-white ring-[#0b1f3a] shadow-xl hover:-translate-y-1 hover:shadow-2xl'
                    : 'bg-[#fafbfd] ring-black/5 text-[#0b1f3a]/65'
                }`}
              >
                {available && (
                  <span className="absolute top-5 right-5 text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-300/30">
                    Live
                  </span>
                )}
                {!available && (
                  <span className="absolute top-5 right-5 text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#0b1f3a]/5 text-[#0b1f3a]/45 ring-1 ring-[#0b1f3a]/10">
                    Soon
                  </span>
                )}
                <Icon className={`w-12 h-12 ${available ? '' : 'opacity-40'}`} />
                <p
                  className={`mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] ${
                    available ? 'text-blue-300' : 'text-[#0b1f3a]/45'
                  }`}
                >
                  {status}
                </p>
                <h3 className={`mt-1 text-2xl md:text-3xl font-black leading-tight ${available ? '' : 'text-[#0b1f3a]/80'}`}>
                  {name}
                </h3>
                {available ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`download-platform-${key}`}
                    className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#0b1f3a] text-sm font-semibold shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                  >
                    {cta}
                    <ArrowRight className="w-4 h-4" />
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.getElementById('waitlist');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    data-testid={`download-platform-${key}`}
                    className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0b1f3a]/5 ring-1 ring-[#0b1f3a]/10 text-[#0b1f3a] text-sm font-semibold hover:bg-[#0b1f3a]/10 transition-colors duration-200"
                  >
                    <Bell className="w-4 h-4" />
                    {cta}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section
        id="waitlist"
        className="px-6 py-20 md:py-24 bg-gradient-to-b from-white to-[#eaf3fb]"
      >
        <div className="max-w-2xl mx-auto">
          {success ? (
            <div className="rounded-3xl bg-white ring-1 ring-emerald-200 shadow-xl p-10 md:p-12 text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-3xl md:text-4xl font-black text-[#0b1f3a] mb-3">
                You&rsquo;re <em className="italic font-light text-blue-600">on the list.</em>
              </h2>
              <p className="text-[#0b1f3a]/70 leading-relaxed">
                We&rsquo;ll write the moment iOS or Windows is ready. No spam, no funnel,
                no nonsense.
              </p>
            </div>
          ) : (
            <div className="rounded-3xl bg-white ring-1 ring-black/5 shadow-xl p-8 md:p-12">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-blue-600 mb-3 text-center">
                For iOS &amp; Windows
              </p>
              <h2 className="text-3xl md:text-4xl font-black text-[#0b1f3a] text-center leading-tight">
                Join the{' '}
                <em className="italic font-light text-blue-600">waitlist.</em>
              </h2>
              <p className="mt-3 text-center text-[#0b1f3a]/65 leading-relaxed">
                Be first to know when we ship.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-[#0b1f3a] mb-2">
                    Full name
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    data-testid="waitlist-fullname"
                    className="w-full px-4 py-3 rounded-xl bg-[#fafbfd] ring-1 ring-black/5 text-[#0b1f3a] placeholder:text-[#0b1f3a]/35 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0b1f3a] mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                    data-testid="waitlist-email"
                    className="w-full px-4 py-3 rounded-xl bg-[#fafbfd] ring-1 ring-black/5 text-[#0b1f3a] placeholder:text-[#0b1f3a]/35 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#0b1f3a] mb-2">
                    Phone
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="relative">
                      <select
                        name="countryCode"
                        value={formData.countryCode}
                        onChange={handleChange}
                        data-testid="waitlist-country-code"
                        className="appearance-none w-full sm:min-w-[180px] px-3 py-3 pr-9 rounded-xl bg-[#fafbfd] ring-1 ring-black/5 text-sm font-medium text-[#0b1f3a] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                      >
                        {COUNTRY_CODES.map((item, i) => (
                          <option key={`${item.code}-${i}`} value={item.code}>
                            {item.code} {item.country}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1f3a]/40 pointer-events-none" />
                    </div>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      placeholder="123 456 7890"
                      required
                      data-testid="waitlist-phone"
                      className="flex-1 px-4 py-3 rounded-xl bg-[#fafbfd] ring-1 ring-black/5 text-[#0b1f3a] placeholder:text-[#0b1f3a]/35 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
                    />
                  </div>
                </div>

                {error && (
                  <div
                    data-testid="waitlist-error"
                    className="bg-rose-50 text-rose-600 px-4 py-3 rounded-xl text-sm ring-1 ring-rose-200"
                  >
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="waitlist-submit"
                  className="w-full inline-flex items-center justify-center gap-2.5 md:gap-3 px-5 md:px-6 py-4 rounded-full bg-[#0b1f3a] text-white text-sm md:text-base font-semibold shadow-md hover:shadow-2xl hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:transform-none transition-all duration-200 whitespace-nowrap"
                >
                  {loading ? (
                    'Submitting…'
                  ) : (
                    <>
                      {/* Shortened label on mobile so the button never wraps
                          to 2 lines on narrow viewports, full copy on ≥sm. */}
                      <span className="sm:hidden">Notify me on launch</span>
                      <span className="hidden sm:inline">Notify me when available</span>
                    </>
                  )}
                  {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
                <p className="text-xs text-[#0b1f3a]/50 text-center">
                  No marketing newsletter — only launch updates. Unsubscribe instantly.
                </p>
              </form>
            </div>
          )}
        </div>
      </section>

      <AltFooter />
    </div>
  );
};

export default DownloadApp;
